import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
// Brand Logo Component using image
const LOGO_URL = "https://customer-assets.emergentagent.com/job_berry-good-design/artifacts/39kugwkr_Screenshot_2026-03-23-19-26-22-58_6012fa4d4ddec268fc5c7112cbb265e7.jpg";

const LogoFull = ({ className = "", dark = false }) => {
  return (
    <img 
      src={LOGO_URL}
      alt="Robert Berries - Frescura que se nota"
      className={`h-12 w-auto object-contain ${className}`}
      data-testid="logo-full"
    />
  );
};

const LogoWhite = ({ className = "" }) => {
  return (
    <img 
      src={LOGO_URL}
      alt="Robert Berries - Frescura que se nota"
      className={`h-12 w-auto object-contain brightness-0 invert ${className}`}
      data-testid="logo-white"
    />
  );
};
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Leaf, 
  Truck, 
  Award, 
  Package, 
  CheckCircle2, 
  Star,
  Instagram,
  Facebook,
  Twitter,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  ArrowRight,
  ChevronRight
} from "lucide-react";

// ============== NAVIGATION ==============
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Nuestras Berries", href: "#berries" },
    { name: "Cómo Funciona", href: "#process" },
    { name: "Ordenar", href: "#plans" },
    { name: "Testimonios", href: "#testimonials" },
  ];

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-white/90 nav-blur shadow-sm py-3" 
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
        <a href="#" data-testid="nav-logo">
          <img 
            src={LOGO_URL}
            alt="Robert Berries"
            className="h-12 w-auto object-contain"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm font-medium transition-colors ${
                scrolled 
                  ? "text-[#1A2740] hover:text-[#C62852]" 
                  : "text-white/90 hover:text-white"
              }`}
            >
              {link.name}
            </a>
          ))}
          <Button 
            data-testid="nav-order-btn"
            className="bg-[#C62852] hover:bg-[#A01D40] text-white rounded-full px-6 py-2 text-sm font-medium shadow-lg transition-transform hover:-translate-y-0.5"
            onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Ordenar
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          data-testid="mobile-menu-btn"
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X className={scrolled ? "text-[#1A2740]" : "text-white"} size={24} />
          ) : (
            <Menu className={scrolled ? "text-[#1A2740]" : "text-white"} size={24} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg border-t">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block text-[#1A2740] py-2 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <Button 
              className="w-full bg-[#C62852] hover:bg-[#A01D40] text-white rounded-full mt-4"
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Ordenar
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// ============== HERO SECTION ==============
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758652561808-a0d9337fd57a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGJsdWViZXJyaWVzJTIwYm93bCUyMGRhcmslMjBtb29kfGVufDB8fHx8MTc3MjA2MjgyOHww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <div className="animate-fade-in-up">
          <span className="inline-block text-[#D4A856] text-sm md:text-base font-medium tracking-widest uppercase mb-4">
            Calidad Premium
          </span>
          
          <h1 
            data-testid="hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight tracking-tight mb-6"
          >
            Berries Premium<br />
            <span className="italic">Entregadas en Tu Puerta</span>
          </h1>
          
          <p 
            data-testid="hero-subheadline"
            className="text-white/80 text-base md:text-lg max-w-xl mx-auto mb-10 font-light"
          >
            Frescas. Seleccionadas a mano. Irresistiblemente buenas.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              data-testid="hero-cta-primary"
              className="bg-[#C62852] hover:bg-[#A01D40] text-white rounded-full px-10 py-6 text-base font-medium shadow-lg btn-shine transition-transform hover:-translate-y-1"
              onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Ordenar Ahora
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button 
              data-testid="hero-cta-secondary"
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10 rounded-full px-10 py-6 text-base font-medium backdrop-blur-sm"
              onClick={() => document.getElementById('berries')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explorar Berries
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== WHY BERRY GOOD SECTION ==============
const WhySection = () => {
  const features = [
    {
      icon: Leaf,
      title: "Del Campo",
      description: "Cosechadas a mano en su punto óptimo de madurez en granjas orgánicas de confianza."
    },
    {
      icon: Truck,
      title: "Entrega Express",
      description: "Del campo a tu puerta en 24 horas, garantizando la máxima frescura."
    },
    {
      icon: Award,
      title: "Calidad Premium",
      description: "Solo las mejores berries pasan nuestros rigurosos estándares de calidad."
    }
  ];

  return (
    <section 
      data-testid="why-section"
      className="py-20 md:py-32 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-[#D4A856] text-sm font-medium tracking-widest uppercase">
            Por Qué Elegirnos
          </span>
          <h2 
            data-testid="why-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2740] mt-3 tracking-tight"
          >
            La Diferencia Berry Good
          </h2>
          <div className="section-divider mt-6" />
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
              className="text-center group animate-fade-in-up"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#4B0F3F]/5 mb-6 group-hover:bg-[#4B0F3F]/10 transition-colors">
                <feature.icon className="text-[#D4A856]" size={28} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl text-[#1A2740] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#4B5563] text-sm leading-relaxed max-w-xs mx-auto">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== OUR BERRIES SECTION ==============
const BerriesSection = () => {
  const berries = [
    {
      name: "Fresas",
      weight: "1 kg",
      price: "₡4,000",
      image: "https://images.unsplash.com/photo-1543158181-e6f9f6712055?w=800&q=80",
      featured: true
    },
    {
      name: "Moras",
      weight: "250 g",
      price: "₡2,000",
      image: "https://images.unsplash.com/photo-1746625132744-65948610b467?w=800&q=80",
      featured: false
    },
    {
      name: "Frambuesas",
      weight: "125 g",
      price: "₡2,000",
      image: "https://images.unsplash.com/photo-1577069861033-55d04cec4ef5?w=800&q=80",
      featured: false
    },
    {
      name: "Arándanos",
      weight: "125 g",
      price: "₡2,000",
      image: "https://images.unsplash.com/photo-1498557850523-fd3d118b962e?w=800&q=80",
      featured: false
    },
    {
      name: "Uchuvas",
      weight: "250 g",
      price: "₡2,000",
      image: "https://images.pexels.com/photos/5015351/pexels-photo-5015351.jpeg?auto=compress&cs=tinysrgb&w=800",
      featured: false
    }
  ];

  return (
    <section 
      id="berries"
      data-testid="berries-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-[#D4A856] text-sm font-medium tracking-widest uppercase">
            Menú de Precios
          </span>
          <h2 
            data-testid="berries-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2740] mt-3 tracking-tight"
          >
            Nuestras Berries Frescas
          </h2>
          <p className="text-[#4B5563] mt-4 max-w-lg mx-auto">
            Frutas frescas de la más alta calidad, directas del campo a tu mesa. Precios en colones.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {berries.map((berry, index) => (
            <Card 
              key={berry.name}
              data-testid={`berry-card-${berry.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`berry-card overflow-hidden border-0 ${
                berry.featured 
                  ? 'lg:col-span-2 bg-[#1A2740]' 
                  : 'bg-white border border-gray-100'
              } rounded-2xl`}
            >
              <div 
                className={`relative ${berry.featured ? 'h-80' : 'h-64'}`}
                style={{
                  backgroundImage: `url(${berry.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className={`absolute inset-0 ${berry.featured ? 'bg-gradient-to-t from-[#1A2740]/95 via-[#1A2740]/50 to-transparent' : 'bg-gradient-to-t from-black/80 via-black/30 to-transparent'}`} />
                
                {/* Weight Badge */}
                <div className="absolute top-4 right-4 bg-[#D4A856] text-[#1A2740] px-3 py-1 rounded-full text-sm font-semibold">
                  {berry.weight}
                </div>
                
                <CardContent className="absolute bottom-0 left-0 right-0 p-6">
                  {berry.featured && (
                    <span className="inline-flex items-center gap-1 text-[#D4A856] text-xs font-medium tracking-widest uppercase mb-2">
                      <Star size={12} fill="#D4A856" />
                      Más Vendida
                    </span>
                  )}
                  <h3 className={`font-serif ${berry.featured ? 'text-2xl md:text-3xl' : 'text-xl'} text-white mb-2`}>
                    {berry.name}
                  </h3>
                  
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-2xl font-serif text-white">
                      {berry.price}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-white/80 bg-white/10 px-3 py-1 rounded-full">
                      <CheckCircle2 size={12} className="text-[#5FA34D]" />
                      Frescura Garantizada
                    </span>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className="text-[#4B5563] mb-4">¿Listo para ordenar?</p>
          <a 
            href="https://wa.me/50687116278"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-1 shadow-lg"
          >
            <Phone size={20} />
            Ordenar por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

// ============== DELIVERY PROCESS SECTION ==============
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Selecciona",
      description: "Elige tus berries favoritas y tu plan de suscripción"
    },
    {
      number: "02",
      title: "Empacamos",
      description: "Empacamos tu pedido con cuidado en envases ecológicos"
    },
    {
      number: "03",
      title: "Entregamos",
      description: "Berries frescas llegan a tu puerta en 24 horas"
    }
  ];

  return (
    <section 
      id="process"
      data-testid="process-section"
      className="py-20 md:py-32 bg-[#F4F4F5]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-[#D4A856] text-sm font-medium tracking-widest uppercase">
            Cómo Funciona
          </span>
          <h2 
            data-testid="process-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2740] mt-3 tracking-tight"
          >
            Del Campo a Tu Mesa, Simplificado
          </h2>
        </div>

        <div className="relative">
          {/* Dashed connector line (desktop) */}
          <div className="hidden md:block absolute top-16 left-1/6 right-1/6 dashed-connector" />
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                data-testid={`process-step-${step.number}`}
                className="relative text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Step circle */}
                <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-full bg-white border-2 border-[#D4A856] mb-6 mx-auto shadow-lg">
                  <div className="text-center">
                    <span className="block text-[#D4A856] text-xs font-medium tracking-wider">
                      PASO
                    </span>
                    <span className="font-serif text-3xl text-[#1A2740]">
                      {step.number}
                    </span>
                  </div>
                </div>
                
                <h3 className="font-serif text-2xl text-[#1A2740] mb-3">
                  {step.title}
                </h3>
                <p className="text-[#4B5563] text-sm max-w-xs mx-auto leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== ORDER SECTION (replaces Plans) ==============
const OrderSection = () => {
  return (
    <section 
      id="plans"
      data-testid="order-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12">
          <span className="text-[#D4A856] text-sm font-medium tracking-widest uppercase">
            Haz Tu Pedido
          </span>
          <h2 
            data-testid="order-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2740] mt-3 tracking-tight"
          >
            ¿Cómo Ordenar?
          </h2>
          <p className="text-[#4B5563] mt-4 max-w-lg mx-auto">
            Ordenar tus berries frescas es muy fácil. Solo contáctanos por WhatsApp y te atendemos de inmediato.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="bg-gradient-to-br from-[#1A2740] to-[#4B0F3F] text-white rounded-3xl overflow-hidden shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#25D366] mb-6">
                  <Phone size={36} className="text-white" />
                </div>
                
                <h3 className="font-serif text-2xl md:text-3xl mb-4">
                  Contáctanos por WhatsApp
                </h3>
                
                <p className="text-white/70 mb-6">
                  Escríbele a <span className="text-[#D4A856] font-medium">Sergio Robert</span> para hacer tu pedido
                </p>
                
                <div className="bg-white/10 rounded-2xl p-6 mb-8">
                  <p className="text-3xl font-serif text-[#D4A856]">
                    +506 8711-6278
                  </p>
                </div>
                
                <a 
                  href="https://wa.me/50687116278?text=Hola%20Sergio!%20Me%20gustaría%20hacer%20un%20pedido%20de%20berries%20frescas"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-order-btn"
                  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full px-10 py-5 text-lg font-medium transition-all hover:-translate-y-1 shadow-lg"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Ordenar Ahora
                </a>
                
                <p className="text-white/50 text-sm mt-6">
                  Respuesta rápida • Entregas en Costa Rica
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// ============== TESTIMONIALS SECTION ==============
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "María García",
      role: "Entusiasta de la Salud",
      image: "https://images.unsplash.com/photo-1580152655133-001957ac825b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGhlYWx0aHklMjB3b21hbiUyMGVhdGluZyUyMGJlcnJpZXN8ZW58MHx8fHwxNzcyMDYyODMzfDA&ixlib=rb-4.1.0&q=85",
      quote: "La frescura es incomparable. Berry Good ha transformado mis batidos matutinos en algo verdaderamente especial.",
      rating: 5
    },
    {
      name: "Carlos Rodríguez",
      role: "Chef Casero",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote: "Como alguien que se toma la cocina en serio, la calidad de estas bayas marca toda la diferencia en mis postres.",
      rating: 5
    },
    {
      name: "Ana Martínez",
      role: "Madre Ocupada",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      quote: "La comodidad de tener berries premium entregadas en mi puerta me ahorra mucho tiempo. ¡A mis hijos les encantan!",
      rating: 5
    }
  ];

  return (
    <section 
      id="testimonials"
      data-testid="testimonials-section"
      className="py-20 md:py-32 bg-[#F4F4F5]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-[#D4A856] text-sm font-medium tracking-widest uppercase">
            Testimonios
          </span>
          <h2 
            data-testid="testimonials-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2740] mt-3 tracking-tight"
          >
            Lo Que Dicen Nuestros Clientes
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={testimonial.name}
              data-testid={`testimonial-${index + 1}`}
              className="testimonial-card bg-white rounded-2xl p-8 border-0 shadow-sm hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-0">
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#D4A856" className="text-[#D4A856]" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-[#4B5563] leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-medium text-[#1A2740]">{testimonial.name}</p>
                    <p className="text-sm text-[#4B5563]">{testimonial.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== NEWSLETTER SECTION ==============
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setTimeout(() => {
      toast.success("¡Bienvenido a la familia Robert Berries!", {
        description: "Revisa tu bandeja de entrada para ofertas exclusivas.",
      });
      setEmail("");
      setLoading(false);
    }, 1000);
  };

  return (
    <section 
      data-testid="newsletter-section"
      className="py-20 md:py-24 bg-[#4B0F3F] noise-texture"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
          Únete a la Familia Robert Berries
        </h2>
        <p className="text-white/70 mb-8 max-w-md mx-auto">
          Suscríbete para recibir ofertas exclusivas, recetas de temporada y las novedades más frescas.
        </p>
        
        <form 
          data-testid="newsletter-form"
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <Input 
            type="email"
            placeholder="Ingresa tu correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full px-6 py-6"
            data-testid="newsletter-email-input"
          />
          <Button 
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit-btn"
            className="bg-[#D4A856] hover:bg-[#C69746] text-[#1A2740] rounded-full px-8 py-6 font-medium"
          >
            {loading ? "Suscribiendo..." : "Suscribirse"}
          </Button>
        </form>
      </div>
    </section>
  );
};

// ============== FOOTER ==============
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const footerLinks = {
    company: [
      { name: "Sobre Nosotros", href: "#" },
      { name: "Nuestras Granjas", href: "#" },
      { name: "Sostenibilidad", href: "#" },
      { name: "Carreras", href: "#" },
    ],
    support: [
      { name: "Preguntas Frecuentes", href: "#" },
      { name: "Info de Envío", href: "#" },
      { name: "Devoluciones", href: "#" },
      { name: "Contáctanos", href: "#" },
    ],
    legal: [
      { name: "Política de Privacidad", href: "#" },
      { name: "Términos de Servicio", href: "#" },
      { name: "Política de Cookies", href: "#" },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
  ];

  return (
    <footer 
      data-testid="footer"
      className="bg-[#1A2740] footer-pattern text-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL}
              alt="Robert Berries"
              className="h-16 w-auto object-contain mb-6"
            />
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-sm">
              Llevando las mejores berries de la naturaleza desde granjas premium hasta tu puerta. Frescura que se nota.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 text-sm">
              <p className="text-white/90 font-medium mb-2">Contacto: Sergio Robert</p>
              <a href="https://wa.me/50687116278" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                <Phone size={16} className="text-[#25D366]" />
                +506 8711-6278
              </a>
              <a href="mailto:hola@berrygood.com" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                <Mail size={16} className="text-[#D4A856]" />
                hola@berrygood.com
              </a>
              <p className="flex items-center gap-3 text-white/70">
                <MapPin size={16} className="text-[#D4A856]" />
                Costa Rica
              </p>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-serif text-lg mb-4">Empresa</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg mb-4">Soporte</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            &copy; {currentYear} Robert Berries. Todos los derechos reservados.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                data-testid={`social-${social.label.toLowerCase()}`}
                aria-label={social.label}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C62852] transition-colors"
              >
                <social.icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============== MAIN HOME PAGE ==============
const Home = () => {
  return (
    <div data-testid="home-page" className="min-h-screen">
      <Navigation />
      <HeroSection />
      <WhySection />
      <BerriesSection />
      <ProcessSection />
      <OrderSection />
      <TestimonialsSection />
      <NewsletterSection />
      <Footer />
    </div>
  );
};

// ============== APP ==============
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#1A2740',
              color: '#FAFAFA',
              border: '1px solid #D4A856',
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
